import { z } from 'zod';

import { defineService } from '@@api/lib/defineService';
import {
    ArticleBuddy,
    ArticleBuddyPostBody,
    ArticleBuddyStatuses,
    ArticleRephrasing,
    ArticleSummary,
    ArticleTranslation,
    ArticleTranslationStatuses,
    ImageAltText,
    KeywordSuggestions,
    ResultChoice,
    SeoTeaser,
    SpecialTeaserSuggestion,
    SpecialTeaserSuggestionPostBody,
    TeaserGeneration,
    TickerSummary,
} from '@@api/services/aiTools/schemas';
import { UnityArticle } from '@@api/services/content/schemas/article';
import { TickerCommon } from '@@api/services/liveticker/schemas/ticker';
import { CreationMetadata, Metadata } from '@@api/services/metadata/schemas';
import config from '@@config';

export const aiToolsService = defineService(
    (defineEndpoint) => ({
        resultChoice: {
            patch: defineEndpoint({
                method: 'PATCH',
                path: '/result-choice',
                query: z.object({
                    id: ResultChoice.shape.id,
                }),
                body: ResultChoice.pick({
                    feedbackEvaluation: true,
                    used: true,
                }).partial(),
                responses: {
                    200: ResultChoice,
                },
            }),
        },
        keywordSuggestions: {
            get: defineEndpoint({
                method: 'GET',
                path: '/keyword-suggestions/articles/:id',
                pathParams: z.object({
                    id: UnityArticle.shape.id,
                }),
                responses: {
                    200: KeywordSuggestions,
                },
            }),
        },
        teaserGenerator: {
            post: defineEndpoint({
                method: 'POST',
                path: '/teaser-generation/articles/:id',
                pathParams: z.object({
                    id: UnityArticle.shape.id,
                }),
                body: z.object({
                    metadataId: z.number(),
                }),
                responses: {
                    201: TeaserGeneration,
                },
            }),
        },
        seoTeaserGenerator: {
            post: defineEndpoint({
                method: 'POST',
                path: '/seo-teaser-generation/articles/:id',
                pathParams: z.object({
                    id: UnityArticle.shape.id,
                }),
                body: z.object({
                    articleBody: z.string(),
                    keywords: z.array(z.string()),
                }),
                responses: {
                    201: SeoTeaser,
                },
            }),
        },
        specialTeaser: {
            post: defineEndpoint({
                method: 'POST',
                path: '/special-teaser/articles/:id',
                pathParams: z.object({
                    id: UnityArticle.shape.id,
                }),
                body: SpecialTeaserSuggestionPostBody,
                responses: {
                    200: SpecialTeaserSuggestion,
                },
            }),
        },
        imageAltTextGenerator: {
            post: defineEndpoint({
                method: 'POST',
                path: '/image-alt-text-generation',
                body: z.object({
                    elvisId: z.string(),
                }),
                responses: {
                    201: ImageAltText,
                },
            }),
        },
        articleSummaryGeneration: {
            post: defineEndpoint({
                method: 'POST',
                path: '/article-summary/article/:id',
                pathParams: z.object({
                    id: UnityArticle.shape.id,
                }),
                body: z.object({
                    metadataId: z.number(),
                }),
                responses: {
                    201: ArticleSummary,
                },
            }),
            get: defineEndpoint({
                method: 'GET',
                path: '/article-summary/article/:id',
                pathParams: z.object({
                    id: UnityArticle.shape.id,
                }),
                responses: {
                    200: ArticleSummary,
                },
            }),
        },
        tickerSummaryGeneration: {
            post: defineEndpoint({
                method: 'POST',
                path: '/ticker-summary/tickers/:id',
                pathParams: z.object({
                    id: TickerCommon.shape.id,
                }),
                query: z.object({
                    type: z.enum(['short', 'long', 'paragraph']),
                }),
                body: z.object({
                    metadataId: z.number(),
                }),
                responses: {
                    201: TickerSummary,
                },
            }),
        },
        articleRephrasing: {
            post: defineEndpoint({
                method: 'POST',
                path: '/rephrasing/articles/:id',
                pathParams: z.object({
                    id: UnityArticle.shape.id,
                }),
                body: z.object({
                    text: z.string(),
                    style: z.literal('natural'),
                }),
                responses: {
                    201: ArticleRephrasing,
                },
            }),
        },
        articleBuddy: {
            getStatuses: defineEndpoint({
                method: 'GET',
                path: '/article-buddy/articles/:id/statuses',
                pathParams: z.object({
                    id: UnityArticle.shape.id,
                }),
                responses: {
                    200: ArticleBuddyStatuses,
                },
            }),
            get: defineEndpoint({
                method: 'GET',
                path: '/article-buddy/articles/:id',
                pathParams: z.object({
                    id: UnityArticle.shape.id,
                }),
                responses: {
                    200: ArticleBuddy,
                },
            }),
            post: defineEndpoint({
                method: 'POST',
                path: '/article-buddy/articles/:id',
                pathParams: z.object({
                    id: UnityArticle.shape.id,
                }),
                body: ArticleBuddyPostBody,
                responses: {
                    201: z.never(),
                },
            }),
        },
        translation: {
            getStatuses: defineEndpoint({
                method: 'GET',
                path: '/translation/article/:id/statuses',
                pathParams: z.object({
                    id: UnityArticle.shape.id,
                }),
                responses: {
                    200: ArticleTranslationStatuses,
                },
            }),
            get: defineEndpoint({
                method: 'GET',
                path: '/translation/article/:id',
                pathParams: z.object({
                    id: UnityArticle.shape.id,
                }),
                responses: {
                    200: ArticleTranslation,
                },
            }),
            start: defineEndpoint({
                method: 'POST',
                path: '/translation/article/:id',
                pathParams: z.object({
                    id: UnityArticle.shape.id,
                }),
                body: z.object({
                    metadataId: Metadata.shape.id,
                    metadata: CreationMetadata.extend({
                        locale: z.string(),
                    }),
                }),
                responses: {
                    202: z.never(),
                },
            }),
        },
    }),
    {
        baseUrl: config.apiEnvVars.aiToolsUrl,
    },
);
